/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2021, 2024
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/
import { Loading } from 'carbon-components-react';
import _ from 'lodash';
import React, { useCallback, useState, useEffect } from 'react';
import { useMount } from 'react-use';

import { useAppSelector } from '@ibm-watson/shared/store';
import { LoaderStates } from '../../../../../ts';
import { selectCatalogItemById } from '../../../store/catalog/selectors';
import { transformOpenAPISpec } from '../../../util/openAPISpecParser';
import { getInvalidResponseMessagesFromSpec } from '../helpers';
import { Endpoints } from './endpoints/Endpoints';

interface MappingContentProps {
  catalogItemId?: string;
  updatingCatalogItemSpecState?: string;
  setUpdatingCatalogItemSpecState?: (string) => void;
}

export const MappingContent: React.FC<MappingContentProps> = ({
  catalogItemId,
  updatingCatalogItemSpecState,
  setUpdatingCatalogItemSpecState
}) => {
  const catalogItem = useAppSelector(state =>
    selectCatalogItemById(state, catalogItemId)
  );
  const openAPISpec = _.cloneDeep(catalogItem?.specification?.body);
  const invalidResponses = getInvalidResponseMessagesFromSpec(openAPISpec);

  // Get transformed OpenAPI spec for its endpoints
  const [transformedOpenAPISpec, setTransformedOpenAPISpec] = useState({
    endpoints: []
  });

  const getTransformedOpenAPISpec = useCallback(async () => {
    const result = await transformOpenAPISpec(openAPISpec);
    setTransformedOpenAPISpec(result);
  }, [openAPISpec]);

  // When we load, we need to get the transformed OpenAPI spec...
  useMount(() => getTransformedOpenAPISpec());

  // When the spec is being uploaded, we must re-transform the OpenAPI spec
  useEffect(() => {
    if (updatingCatalogItemSpecState === LoaderStates.SUCCESS) {
      setUpdatingCatalogItemSpecState(LoaderStates.IDLE);
      getTransformedOpenAPISpec();
    }
  }, [
    getTransformedOpenAPISpec,
    setUpdatingCatalogItemSpecState,
    updatingCatalogItemSpecState
  ]);

  return (
    <div className="CatalogOpenApiDataTable">
      {_.isEmpty(transformedOpenAPISpec.endpoints) ||
      updatingCatalogItemSpecState === LoaderStates.ACTIVE ? (
        <Loading />
      ) : (
        <Endpoints
          invalidResponses={invalidResponses}
          endpoints={transformedOpenAPISpec.endpoints}
        />
      )}
    </div>
  );
};
